import React, { useState } from 'react';
import { FiLock, FiEye, FiTrash2, FiMoreVertical } from 'react-icons/fi';
import { useConfirm } from '../ui/ConfirmDialog';

const BoardSelector = ({ boards = [], selectedBoard, onBoardSelect, onBoardDelete, canEdit = false }) => {
  const confirm = useConfirm();
  const [showOptions, setShowOptions] = useState(null);

  const handleDeleteClick = async (e, board) => {
    e.stopPropagation();
    setShowOptions(null);
    const ok = await confirm({
      title: 'Delete this board?',
      description: 'This cannot be undone and will delete all tasks in this board.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    onBoardDelete(board.id);
  };

  const toggleOptions = (e, boardId) => {
    e.stopPropagation();
    setShowOptions(showOptions === boardId ? null : boardId);
  };

  if (!Array.isArray(boards) || boards.length === 0) {
    return (
      <div className="text-center py-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No task boards found
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {boards.map((board) => (
        <button
          key={board.id}
          onClick={() => onBoardSelect(board)}
          className={`w-full text-left p-2 rounded-md transition-colors ${
            selectedBoard?.id === board.id
              ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
              : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
          }`}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: board.color }}
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                {board.name}
              </h4>
              {board.description && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {board.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {board.is_private ? (
                <FiLock className="w-3 h-3 text-gray-400" />
              ) : (
                <FiEye className="w-3 h-3 text-gray-400" />
              )}
              {canEdit && (
                <div className="relative">
                  <button
                    onClick={(e) => toggleOptions(e, board.id)}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <FiMoreVertical className="w-3 h-3 text-gray-400" />
                  </button>
                  {showOptions === board.id && (
                    <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[110px] overflow-hidden">
                      <button
                        onClick={(e) => handleDeleteClick(e, board)}
                        className="w-full px-2.5 py-1.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1.5"
                      >
                        <FiTrash2 className="w-3 h-3" />
                        Delete Board
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            <span className="truncate">
              {board.owner?.firstName} {board.owner?.lastName}
            </span>
            <span className="capitalize flex-shrink-0">
              {board.view_mode || 'list'}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
};

export default BoardSelector;
